import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getPayments, 
  updatePaymentStatus,
  resendPaymentNotification
} from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye, 
  FiCheckCircle, 
  FiXCircle, 
  FiArrowLeft, 
  FiArrowRight,
  FiDownload,
  FiAlertCircle,
  FiMail,
  FiDollarSign,
  FiCreditCard,
  FiSend,
  FiExternalLink
} from 'react-icons/fi';
import { 
  TbArrowsExchange,
  TbReceiptRefund,
  TbWallet
} from 'react-icons/tb';

function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    payment_type: '',
    gateway: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchPayments();
  }, [filters, pagination.page]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await getPayments(params);
      if (response.success) {
        setPayments(response.data.payments || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
      } else {
        setError(response.message || 'Failed to load payments');
      }
    } catch (err) {
      setError('Failed to load payments: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusUpdate = async (paymentId, newStatus) => {
    if (!window.confirm(`Are you sure you want to change payment status to "${newStatus}"?`)) {
      return;
    }

    try {
      const adminNotes = prompt('Enter admin notes (optional):', '');
      const response = await updatePaymentStatus(paymentId, {
        status: newStatus,
        admin_notes: adminNotes || ''
      });

      if (response.success) {
        setSuccessMessage(`Payment status updated to ${newStatus} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchPayments(); // Refresh the list
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError(`Failed to update payment status: ${err.message}`);
    }
  };

  const handleResendNotification = async (paymentId) => {
    if (!window.confirm('Resend payment notification to user?')) {
      return;
    }

    try {
      const response = await resendPaymentNotification(paymentId);
      if (response.success) {
        setSuccessMessage('Payment notification resent successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(response.message || 'Resend failed');
      }
    } catch (err) {
      setError(`Failed to resend notification: ${err.message}`);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full flex items-center justify-center";
    
    const statusConfig = {
      'success': { bg: 'bg-green-100', text: 'text-green-800', label: 'Success', icon: '✓' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending', icon: '⏳' },
      'failed': { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed', icon: '✗' },
      'cancelled': { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Cancelled', icon: '⊘' },
      'refunded': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Refunded', icon: '↺' },
      'processing': { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Processing', icon: '⚙️' }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: '?' };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const getPaymentTypeBadge = (paymentType, metadata) => {
    let parsedMetadata = null;
    try {
      parsedMetadata = metadata ? JSON.parse(metadata) : null;
    } catch (e) {
      parsedMetadata = null;
    }
  
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full flex items-center";
    
    // Define badge configurations for each payment type
    const badgeConfigs = {
      'deposit': {
        icon: <TbWallet className="mr-1" />,
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Deposit'
      },
      'withdrawal': {
        icon: <TbWallet className="mr-1" />,
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        label: 'Withdrawal'
      },
      'purchase': {
        icon: <FiCreditCard className="mr-1" />,
        bg: 'bg-indigo-100',
        text: 'text-indigo-800',
        label: 'Purchase'
      },
      'boost': {
        icon: <TbArrowsExchange className="mr-1" />,
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        label: 'Boost'
      },
      'escrow': {
        icon: <TbReceiptRefund className="mr-1" />,
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        label: 'Escrow'
      }
    };
  
    // Handle old order payment metadata (for backward compatibility)
    if (parsedMetadata?.payment_type === 'order' || parsedMetadata?.payment_type === 'order_payment') {
      return (
        <span className={`${baseClasses} bg-indigo-100 text-indigo-800`}>
          <FiCreditCard className="mr-1" />
          Order Payment
        </span>
      );
    }
  
    // Get config for the payment type
    const config = badgeConfigs[paymentType] || {
      icon: null,
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      label: paymentType || 'Unknown'
    };
  
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getGatewayBadge = (gateway) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    
    if (gateway === 'paystack') {
      return <span className={`${baseClasses} bg-orange-100 text-orange-800`}>Paystack</span>;
    } else if (gateway === 'flutterwave') {
      return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Flutterwave</span>;
    } else if (gateway === 'stripe') {
      return <span className={`${baseClasses} bg-purple-100 text-purple-800`}>Stripe</span>;
    }
    
    return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{gateway}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseMetadata = (metadata) => {
    try {
      return metadata ? JSON.parse(metadata) : null;
    } catch (e) {
      return null;
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      payment_type: '',
      gateway: '',
      date_from: '',
      date_to: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Loading Skeleton
  if (loading && payments.length === 0) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-10 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-48"></div>
                    <div className="h-3 bg-gray-100 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-100 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f5f5f5] p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0f0e0e]">Payment Management</h1>
            <p className="text-gray-600 mt-1">Monitor and manage payment transactions</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/finance/reports')}
              className="px-4 py-2.5 bg-white text-[#0f0e0e] border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium flex items-center"
            >
              <FiDownload className="mr-2" />
              Reports
            </button>
            <button
              onClick={fetchPayments}
              className="px-4 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-all duration-200 font-medium flex items-center"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-500 text-xl" />
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between animate-fade-in">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-500 text-xl" />
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-800 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Payments</p>
              <p className="text-2xl font-bold text-[#0f0e0e] mt-1">{pagination.total}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
              <FiCreditCard className="text-blue-600 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Successful</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {payments.filter(p => p.status === 'success').length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
              <FiCheckCircle className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">
                {payments.filter(p => p.status === 'pending').length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-yellow-50 flex items-center justify-center">
              <FiAlertCircle className="text-yellow-600 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Amount</p>
              <p className="text-2xl font-bold text-[#0f0e0e] mt-1">
                {formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0))}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
              <FiDollarSign className="text-purple-600 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-[#0f0e0e]">Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-[#e55c0d] font-medium flex items-center"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Reference, user, email..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refunded">Refunded</option>
                  <option value="processing">Processing</option>
                </select>
              </div>

             
              {/* Payment Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
                <select
                  value={filters.payment_type}
                  onChange={(e) => handleFilterChange('payment_type', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Types</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                  <option value="purchase">Purchase</option>
                  <option value="boost">Boost</option>
                  <option value="escrow">Escrow</option>
                </select>
              </div>

              {/* Gateway Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gateway</label>
                <select
                  value={filters.gateway}
                  onChange={(e) => handleFilterChange('gateway', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                >
                  <option value="">All Gateways</option>
                  <option value="paystack">Paystack</option>
                  <option value="flutterwave">Flutterwave</option>
                  <option value="stripe">Stripe</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium hover:bg-gray-50 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchPayments}
                  className="px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-colors font-medium flex items-center"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Information
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount & Fees
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status & Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {payments.map((payment) => {
                const metadata = parseMetadata(payment.metadata);
                const isOrderPayment = metadata?.payment_type === 'order';
                
                return (
                  <tr key={payment.payment_id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          {getGatewayBadge(payment.gateway)}
                          {getPaymentTypeBadge(payment.payment_type, payment.metadata)}
                        </div>
                        <div className="text-sm font-semibold text-[#0f0e0e] font-mono">
                          {payment.reference}
                        </div>
                        <div className="text-sm text-gray-600 mt-1 truncate max-w-xs">
                          {metadata?.description || 
                            (payment.payment_type === 'boost' ? 'Boost package purchase' :
                            payment.payment_type === 'escrow' ? 'Escrow payment' :
                            payment.payment_type === 'purchase' ? 'Order payment' :
                            'No description')
                          }
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          ID: {payment.payment_id}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#ff660e] to-orange-400 flex items-center justify-center text-white text-xs font-bold">
                            {payment.first_name?.charAt(0)}{payment.last_name?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-[#0f0e0e]">
                              {payment.first_name} {payment.last_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {payment.user_id}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 flex items-center">
                          <FiMail className="mr-2 text-gray-400" />
                          {payment.user_email}
                        </div>
                        
                        {/* Show boost/escrow details if available */}
                        {metadata?.boost_order_id && (
                          <div className="text-xs text-purple-600 font-medium mt-1 flex items-center">
                            <TbArrowsExchange className="mr-1" />
                            Boost Order: #{metadata.boost_order_id}
                          </div>
                        )}
                        {metadata?.escrow_id && (
                          <div className="text-xs text-amber-600 font-medium mt-1 flex items-center">
                            <TbReceiptRefund className="mr-1" />
                            Escrow: #{metadata.escrow_id}
                          </div>
                        )}
                        {metadata?.listing_id && payment.payment_type === 'boost' && (
                          <div className="text-xs text-gray-400 truncate max-w-xs">
                            Listing: #{metadata.listing_id}
                          </div>
                        )}
                        {metadata?.conversation_id && payment.payment_type === 'escrow' && (
                          <div className="text-xs text-gray-400 truncate max-w-xs">
                            Conversation: #{metadata.conversation_id}
                          </div>
                        )}
                        {metadata?.order_id && payment.payment_type === 'purchase' && (
                          <div className="text-xs text-gray-400 truncate max-w-xs">
                            Order #{metadata.order_id}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="text-xl font-bold text-[#0f0e0e]">
                          {formatCurrency(payment.amount)}
                        </div>
                        <div className="text-xs text-gray-500 space-y-1">
                          {payment.net_amount !== '0.00' && (
                            <div className="flex justify-between">
                              <span>Net Amount:</span>
                              <span className="font-medium">{formatCurrency(payment.net_amount)}</span>
                            </div>
                          )}
                          {payment.platform_fee !== '0.00' && (
                            <div className="flex justify-between">
                              <span>Platform Fee:</span>
                              <span>{formatCurrency(payment.platform_fee)}</span>
                            </div>
                          )}
                          {payment.processor_fee !== '0.00' && (
                            <div className="flex justify-between">
                              <span>Processor Fee:</span>
                              <span>{formatCurrency(payment.processor_fee)}</span>
                            </div>
                          )}
                          {metadata?.fee_breakdown?.total_fees && (
                            <div className="flex justify-between">
                              <span>Total Fees:</span>
                              <span className="font-medium text-red-600">
                                {formatCurrency(metadata.fee_breakdown.total_fees)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start space-y-2">
                        {getStatusBadge(payment.status)}
                        {payment.gateway_response && (
                          <button
                            onClick={() => {
                              try {
                                const gatewayResp = JSON.parse(payment.gateway_response);
                                console.log('Gateway Response:', gatewayResp);
                                alert('Gateway response logged to console');
                              } catch (e) {
                                alert('Error parsing gateway response');
                              }
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                          >
                            <FiExternalLink className="mr-1" />
                            Gateway Details
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm space-y-1">
                        <div className="font-medium text-[#0f0e0e]">
                          {formatDate(payment.created_at)}
                        </div>
                        <div className="text-gray-500">
                          {formatTime(payment.created_at)}
                        </div>
                        {payment.updated_at !== payment.created_at && (
                          <div className="text-xs text-gray-400">
                            Updated: {formatDate(payment.updated_at)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-2 min-w-[180px]">
                        <button
                          onClick={() => navigate(`/admin/finance/payments/${payment.payment_id}`)}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center justify-center transition-colors"
                        >
                          <FiEye className="mr-2" />
                          View Details
                        </button>
                        
                        <div className="flex flex-wrap gap-1">
                          {payment.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleStatusUpdate(payment.payment_id, 'success')}
                                className="px-3 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium border border-green-200 flex-1"
                              >
                                <FiCheckCircle className="inline mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleStatusUpdate(payment.payment_id, 'failed')}
                                className="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-medium border border-red-200 flex-1"
                              >
                                <FiXCircle className="inline mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                          {payment.status === 'success' && (
                            <button
                              onClick={() => handleStatusUpdate(payment.payment_id, 'refunded')}
                              className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-medium border border-blue-200 flex-1"
                            >
                              <TbReceiptRefund className="inline mr-1" />
                              Refund
                            </button>
                          )}
                          {payment.status === 'pending' && (
                            <button
                              onClick={() => handleResendNotification(payment.payment_id)}
                              className="px-3 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded text-xs font-medium border border-orange-200 flex-1"
                            >
                              <FiSend className="inline mr-1" />
                              Resend
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {payments.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-300 text-8xl mb-6">💳</div>
            <h3 className="text-xl font-medium text-gray-700 mb-2">No payments found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters to find what you're looking for.</p>
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55c0d] transition-colors font-medium"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Showing <span className="font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold">{pagination.total}</span> payments
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <FiArrowLeft className="mr-2" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium ${
                          pagination.page === pageNum
                            ? 'bg-[#ff660e] text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Next
                  <FiArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Payments;